import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { IoArrowBack } from "react-icons/io5";

import MobileViewContainer from "../../../Components/MobileViewContainer";
import { LoadingSpinner, EmptyMessage } from "../../../Components/Common";
import { tableOrderService } from "../../../api/services/table-order.service";
import { formatDate, formatOrderId } from "../../../utils/formatters";

const fulfillmentTypeLabels: Record<string, string> = {
  INSTANT: "즉시 제공",
  KITCHEN: "제조 필요",
};

const statusLabels: Record<string, string> = {
  NEW: "주문접수",
  ACCEPTED: "접수완료",
  SERVED: "완료",
  CANCELED: "취소완료",
};

const manufacturingStatusLabels: Record<string, string> = {
  NEW: "제조 대기",
  ACCEPTED: "제조중",
  SERVED: "제조완료",
  CANCELED: "취소완료",
};

const manufacturingStatusStyles: Record<string, string> = {
  NEW: "bg-amber-50 text-amber-700 ring-1 ring-amber-200",
  ACCEPTED: "bg-sky-50 text-sky-700 ring-1 ring-sky-200",
  SERVED: "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200",
  CANCELED: "bg-rose-50 text-rose-700 ring-1 ring-rose-200",
};

const manufacturingSteps = [
  { key: "NEW", label: "주문 접수" },
  { key: "ACCEPTED", label: "제조중" },
  { key: "SERVED", label: "제공 완료" },
];

const formatTableLabel = (order: any) => {
  if (order.table_name && order.table_code) {
    return `${order.table_name} (${order.table_code})`;
  }
  if (order.order_type === "pickup") {
    return "픽업 주문";
  }
  return "테이블 미지정";
};

const formatDateTime = (value?: string | null) => {
  if (!value) {
    return "-";
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return date.toLocaleString();
};

const getCompletedAt = (order: any) => {
  if (!order) {
    return null;
  }

  if (order.status === "SERVED") {
    return order.served_at || order.paid_at || null;
  }
  if (order.status === "CANCELED") {
    return order.canceled_at || order.canceled_payment_at || null;
  }
  if (order.fulfillment_type === "INSTANT") {
    return order.paid_at || order.served_at || null;
  }
  return null;
};

const isKitchenOrder = (order: any) => order?.fulfillment_type === "KITCHEN";

const getOrderStatusLabel = (order: any) => {
  if (!order) {
    return "-";
  }

  if (isKitchenOrder(order)) {
    return manufacturingStatusLabels[order.status] || order.status;
  }

  return statusLabels[order.status] || order.status;
};

const getManufacturingStepTime = (order: any, stepKey: string) => {
  if (stepKey === "NEW") {
    return order.created_at;
  }
  if (stepKey === "ACCEPTED") {
    return order.accepted_at;
  }
  if (stepKey === "SERVED") {
    return order.served_at;
  }
  return null;
};

const getManufacturingStepState = (order: any, stepKey: string) => {
  if (order.status === "CANCELED") {
    return getManufacturingStepTime(order, stepKey) ? "done" : "idle";
  }

  const currentIndex = manufacturingSteps.findIndex((step) => step.key === order.status);
  const stepIndex = manufacturingSteps.findIndex((step) => step.key === stepKey);

  if (stepIndex < currentIndex) {
    return "done";
  }
  if (stepIndex === currentIndex) {
    return "active";
  }
  return "idle";
};

const getManufacturingStatusClass = (status: string) =>
  manufacturingStatusStyles[status] || "bg-gray-100 text-gray-700 ring-1 ring-gray-200";

export default function TableOrderDetail() {
  const navigate = useNavigate();
  const { id } = useParams();

  const query = useQuery({
    queryKey: ["table-order-detail", id],
    queryFn: async () => {
      const response = await tableOrderService.getMyOrderDetail(Number(id));
      return response.data;
    },
    enabled: Boolean(id),
  });

  const order = query.data;
  const completedAt = getCompletedAt(order);
  const showManufacturingStatus = isKitchenOrder(order);
  const orderStatusLabel = getOrderStatusLabel(order);

  return (
    <MobileViewContainer>
      <div className="min-h-screen bg-gray-50 pb-24">
        <div className="sticky top-0 z-10 flex items-center gap-2 bg-white px-4 py-3 shadow-[0_1px_0_#eee]">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="flex h-8 w-8 items-center justify-center rounded-full hover:bg-gray-100"
          >
            <IoArrowBack size={20} />
          </button>
          <h1 className="text-base font-semibold" style={{ fontFamily: "Gmarket-M" }}>
            {showManufacturingStatus ? "주문 현황" : "오더 내역"}
          </h1>
        </div>

        {query.isLoading ? (
          <LoadingSpinner height="240px" />
        ) : query.isError || !order ? (
          <EmptyMessage message="주문 내역을 불러오지 못했습니다." />
        ) : (
          <div className="space-y-4 px-4 py-4">
            <section className="rounded-2xl bg-white p-4 shadow-sm">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="text-xs text-gray-500">{formatOrderId(order.id, 8, "O-")}</p>
                  <h2 className="mt-1 text-lg font-semibold text-gray-900">
                    {orderStatusLabel}
                  </h2>
                </div>
                <div
                  className={`rounded-full px-3 py-1 text-xs font-semibold ${
                    showManufacturingStatus
                      ? getManufacturingStatusClass(order.status)
                      : "bg-gray-100 text-gray-700"
                  }`}
                >
                  {fulfillmentTypeLabels[order.fulfillment_type || ""] || order.fulfillment_type || "타입 미지정"}
                </div>
              </div>
              <div className="mt-4 grid grid-cols-1 gap-3 text-sm text-gray-700">
                <InfoRow label="주문 유형" value={order.order_type === "pickup" ? "픽업" : "서빙"} />
                <InfoRow label="테이블" value={formatTableLabel(order)} />
                <InfoRow label="주문일시" value={formatDate(order.created_at)} />
                <InfoRow label="상세 시각" value={formatDateTime(order.created_at)} />
                <InfoRow label="완료 시각" value={formatDateTime(completedAt)} />
                <InfoRow label="총 금액" value={`${order.total_amount.toLocaleString()}원`} />
              </div>
              {order.memo ? (
                <div className="mt-4 rounded-xl bg-gray-50 px-3 py-2 text-sm text-gray-700">
                  메모: {order.memo}
                </div>
              ) : null}
            </section>

            {showManufacturingStatus ? (
              <section className="rounded-2xl bg-white p-4 shadow-sm">
                <div className="flex items-center justify-between gap-3">
                  <h3 className="text-sm font-semibold text-gray-900">제조 상태</h3>
                  <span className={`rounded-full px-3 py-1 text-xs font-semibold ${getManufacturingStatusClass(order.status)}`}>
                    {orderStatusLabel}
                  </span>
                </div>

                <div className="mt-4 space-y-3">
                  {manufacturingSteps.map((step) => {
                    const state = getManufacturingStepState(order, step.key);
                    const time = getManufacturingStepTime(order, step.key);

                    return (
                      <div key={step.key} className="flex items-start gap-3">
                        <span
                          className={`mt-0.5 h-3 w-3 shrink-0 rounded-full ${
                            state === "done"
                              ? "bg-emerald-500"
                              : state === "active"
                                ? "bg-sky-500"
                                : "bg-gray-200"
                          }`}
                        />
                        <div className="min-w-0 flex-1 border-b border-gray-100 pb-3 last:border-b-0 last:pb-0">
                          <div className="flex items-center justify-between gap-3">
                            <p className="text-sm font-semibold text-gray-900">{step.label}</p>
                            {state === "active" ? (
                              <span className="rounded-full bg-sky-50 px-2.5 py-1 text-xs font-semibold text-sky-700">
                                현재 단계
                              </span>
                            ) : null}
                          </div>
                          <p className="mt-1 text-xs text-gray-500">{formatDateTime(time)}</p>
                        </div>
                      </div>
                    );
                  })}

                  {order.status === "CANCELED" ? (
                    <div className="rounded-xl bg-rose-50 px-3 py-2 text-sm font-semibold text-rose-700">
                      주문이 취소되었습니다.
                    </div>
                  ) : null}
                </div>
              </section>
            ) : null}

            <section className="rounded-2xl bg-white p-4 shadow-sm">
              <h3 className="text-sm font-semibold text-gray-900">주문 상품</h3>
              <div className="mt-3 space-y-3">
                {order.items?.map((item: any) => (
                  <div key={item.id} className="rounded-xl border border-gray-100 px-3 py-3">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="text-sm font-semibold text-gray-900">{item.menu_name_snapshot}</p>
                        <p className="mt-1 text-xs text-gray-500">
                          수량 {item.quantity}개
                        </p>
                      </div>
                      <div className="shrink-0 text-right">
                        {showManufacturingStatus ? (
                          <span className={`mb-1 inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${getManufacturingStatusClass(order.status)}`}>
                            {orderStatusLabel}
                          </span>
                        ) : null}
                        <p className="text-sm font-semibold text-gray-900">
                          {item.line_amount.toLocaleString()}원
                        </p>
                      </div>
                    </div>
                    {item.options?.length > 0 ? (
                      <div className="mt-2 flex flex-wrap gap-2">
                        {item.options.map((option: any) => (
                          <span
                            key={option.id}
                            className="rounded-full bg-gray-100 px-2.5 py-1 text-xs text-gray-600"
                          >
                            {option.option_name_snapshot} x{option.quantity}
                          </span>
                        ))}
                      </div>
                    ) : null}
                  </div>
                ))}
              </div>
            </section>
          </div>
        )}
      </div>
    </MobileViewContainer>
  );
}

function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between gap-3 border-b border-gray-100 pb-2 last:border-b-0 last:pb-0">
      <span className="text-gray-500">{label}</span>
      <span className="text-right text-gray-900">{value}</span>
    </div>
  );
}
